import { ReactNode } from 'react'

import {
  Button,
  DownloadIcon,
  EditIcon,
  EyeIcon,
  Skeleton,
  Text,
  Tooltip,
} from '@mercai/clever'
import { downloadFile, getFileNameAndExtension } from '@/helpers'

import { FileInfoProps } from './file-info.type'

import styles from './file-info.module.css'
import { getExtensionClasses } from './file-info.constants'

export const FileInfo = ({
  isLoading,
  title,
  file,
  onUpdate,
  className = '',
  showPreview = true,
}: FileInfoProps) => {
  const getFileValue = (file: string) => {
    if (!file) {
      return {}
    }

    const { fileName, extension } = getFileNameAndExtension(file)

    return {
      name: `${fileName}.${extension}`,
      url: file as string,
      extension,
    }
  }

  const getContentName = (name: string): ReactNode => {
    if (name.length > 22) {
      return (
        <Tooltip
          text={name}
          orientation="bottom"
        >{`${name.slice(0, 22)}...`}</Tooltip>
      )
    }

    return name
  }

  const handleDownloadFile = async (url: string, fileName: string) => {
    if (!url) return null

    downloadFile(url, fileName)
  }

  const { name, extension, url } = getFileValue(file)

  if (isLoading) {
    return (
      <div className={`${className} ${styles['file-box']}`}>
        <div className={styles['file-info-box']}>
          <Skeleton height="3rem" width="3rem" />
          <div className={styles['file-info']}>
            <Skeleton height="1rem" width="9rem" />
            <Skeleton height="1rem" width="6rem" />
          </div>
        </div>

        <div className={styles['file-actions-box']}>
          <Skeleton height="2.6rem" width="2.6rem" />
          <Skeleton height="2.6rem" width="2.6rem" />
        </div>
      </div>
    )
  }

  return (
    <div className={`${className} ${styles['file-box']}`}>
      <div className={styles['file-info-box']}>
        {extension && (
          <div className={getExtensionClasses(extension)}>{extension}</div>
        )}
        <div className={styles['file-info']}>
          <Text variant="text-base" className={styles['file-title']}>
            {getContentName(title || '')}
          </Text>
          <Text variant="text-xsmall" className={styles['file-subtitle']}>
            {getContentName(name || '')}
          </Text>
        </div>
      </div>

      <div className={styles['file-actions-box']}>
        {onUpdate && (
          <Button onClick={onUpdate} variant="secondary" model="square">
            {<EditIcon />}
          </Button>
        )}
        {url && (
          <>
            {showPreview && (
              <Button
                href={url}
                target="_blank"
                variant="secondary"
                model="square"
              >
                {<EyeIcon />}
              </Button>
            )}

            <Button
              variant="success-light"
              model="square"
              onClick={() => handleDownloadFile(url, name || '')}
            >
              {<DownloadIcon />}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}
